import {useMemo, useState} from 'react';
import {useMutation, useQuery} from '@apollo/client';
import {
  Alert,
  Form,
  Radio,
  Select,
  Switch,
  Table,
  Tag,
  Upload,
  Button as AntdButton,
} from 'antd';
import {Button} from '@dabafinance/react-components';
import {toast} from 'react-toastify';
import SubmitButton from 'components/Startup/components/SubmitButton';
import {UploadDocumentToBucket} from 'pages/campaigns/new/screens/legalDocument/uploadDocument';
import cloudIcon from 'images/cloud-upload.svg';
import trashIcon from 'images/trash-can-outline.png';
import {CREATE_PICK_SET, GET_LISTED_INSTRUMENTS} from '../resolvers';
import {
  LANGUAGE_OPTIONS,
  MAX_PICKS_PER_REPORT,
  PICK_TYPES,
  SOURCE_OPTIONS,
  isGqlError,
  readMutation,
  sourceLabel,
  typeLabel,
} from '../constants';
import {buildPickInput, PickFields} from './PickFields';
import {buildCleanedCsv, downloadSampleCsv, validateCsv} from './csv';
import styles from './workspace.module.scss';

const isDev = String(process.env.REACT_APP_API_BASE_URL || '').includes(
  '.daba-dev.',
);
const BUCKET = isDev
  ? 'dabafinancialinstruments-nonprod'
  : 'dabafinancialinstruments-prod';

/**
 * Registers a source and upserts a whole BUY/HOLD/SELL set.
 *
 * This is the only operation that can register a source on the parent or move
 * the default, so it is also the way a new source's first pick is created.
 * Submitted rows are an upsert, not a replacement set — instruments left out
 * stay where they are and have to be removed explicitly.
 */
export const SourcePicksModal = ({
  form,
  recommendation,
  mode,
  source,
  onClose,
  onSaved,
}) => {
  const isNewSource = mode === 'NEW_SOURCE';
  const [entryMode, setEntryMode] = useState('MANUAL');
  const [csvFile, setCsvFile] = useState(null);
  const [csvResult, setCsvResult] = useState(null);
  const [uploading, setUploading] = useState(false);

  const [createPickSet, {loading}] = useMutation(CREATE_PICK_SET, {
    errorPolicy: 'all',
  });

  const registeredSources = recommendation?.availableSources || [];
  const registeredSource = registeredSources.find(
    entry => entry.name === source,
  );
  const sourceOptions = useMemo(
    () =>
      SOURCE_OPTIONS.map(option => ({
        ...option,
        disabled:
          isNewSource &&
          registeredSources.some(entry => entry.name === option.value),
      })),
    [isNewSource, registeredSources],
  );

  // Best-effort name resolution for the CSV preview. A row that does not match
  // is not necessarily wrong — only the first page of instruments is loaded —
  // but a wrong-exchange or wrong-product id usually shows up here first.
  const {data: instrumentData} = useQuery(GET_LISTED_INSTRUMENTS, {
    variables: {
      exchangeCode: recommendation?.exchange?.code,
      searchTerm: null,
      page: 1,
      limit: 100,
    },
    skip: entryMode !== 'CSV' || !csvResult || !recommendation?.exchange?.code,
    errorPolicy: 'all',
  });

  const instrumentsById = useMemo(() => {
    const result = instrumentData?.adminGetStockExchangeListedInstruments;
    if (isGqlError(result)) return {};
    return (result?.instruments || []).reduce((accumulator, instrument) => {
      accumulator[instrument.id] = instrument;
      return accumulator;
    }, {});
  }, [instrumentData]);

  /** Manual rows and CSV are mutually exclusive — switching clears the other. */
  const handleEntryModeChange = nextMode => {
    setEntryMode(nextMode);
    if (nextMode === 'CSV') form.setFieldsValue({picks: undefined});
    else {
      setCsvFile(null);
      setCsvResult(null);
    }
  };

  const handleCsvSelected = info => {
    const file = info.file?.originFileObj || info.file;
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setCsvFile(file);
      setCsvResult(validateCsv(String(reader.result || '')));
    };
    reader.onerror = () => toast.error('Could not read that file');
    reader.readAsText(file);
  };

  const csvRowErrors = csvResult?.rows?.filter(row => row.errors.length) || [];
  const csvIsValid =
    Boolean(csvResult) &&
    !csvResult.fileErrors.length &&
    !csvRowErrors.length &&
    csvResult.rows.length > 0;

  const validateManualPicks = picks => {
    if (!picks?.length) {
      toast.error('Add at least one pick');
      return false;
    }
    const ids = picks.map(pick => pick?.instrumentId);
    if (ids.some((id, index) => ids.indexOf(id) !== index)) {
      toast.error('The same instrument appears more than once');
      return false;
    }
    return true;
  };

  const submit = async values => {
    if (entryMode === 'MANUAL' && !validateManualPicks(values.picks)) return;
    if (entryMode === 'CSV' && !csvIsValid) {
      toast.error('Fix the CSV problems listed below first');
      return;
    }

    let csvUrl = null;
    if (entryMode === 'CSV') {
      try {
        setUploading(true);
        // Upload the re-serialized rows rather than the original file, so the
        // trailing blank lines the server parser reads as empty picks are gone.
        const cleaned = new File(
          [buildCleanedCsv(csvResult.rows)],
          csvFile.name,
          {type: 'text/csv'},
        );
        csvUrl = await UploadDocumentToBucket(
          cleaned,
          () => {},
          BUCKET,
          true,
          'recommendations',
        );
      } catch (uploadError) {
        toast.error('Could not upload the CSV file');
        return;
      } finally {
        setUploading(false);
      }
    }

    const exchangeId = recommendation?.exchange?.id;
    createPickSet({
      variables: {
        input: {
          instrumentRecommendationId: recommendation.id,
          source: {
            name: values.source,
            // Only send a logo when there is one — an explicit null on a bulk
            // upsert would clear the logo the source already has.
            ...(values.imageUrl ? {imageUrl: values.imageUrl} : {}),
          },
          type: values.type,
          language: values.language,
          defaultSource: Boolean(values.defaultSource),
          ...(entryMode === 'CSV'
            ? {csv: csvUrl}
            : {
                picks: values.picks.map(pick =>
                  buildPickInput(pick, exchangeId),
                ),
              }),
        },
      },
    })
      .then(result => {
        const {payload, error: failure} = readMutation(
          result,
          'createInstrumentRecommendationPick',
        );
        if (failure) return toast.error(failure);
        toast.success(payload.message);
        onSaved?.({source: values.source, type: values.type});
        onClose();
      })
      .catch(error => toast.error(error?.message));
  };

  const csvPreviewColumns = [
    {title: 'Line', dataIndex: 'line', width: 70},
    {
      title: 'Instrument',
      dataIndex: 'instrumentId',
      render: (instrumentId, row) => {
        const instrument = instrumentsById[instrumentId];
        return (
          <div className={styles.nameCell}>
            <span className={styles.primaryText}>
              {instrument
                ? `${instrument.ticker} · ${instrument.name}`
                : instrumentId}
            </span>
            {!instrument && (
              <span className={styles.mutedText}>
                Not resolved locally — the backend verifies it belongs to{' '}
                {recommendation?.exchange?.code}
              </span>
            )}
            {row.errors.length > 0 && (
              <span className={styles.errorText}>{row.errors.join('; ')}</span>
            )}
          </div>
        );
      },
    },
    {title: 'Price as of', dataIndex: 'priceAsOf', width: 110},
    {title: 'Target', dataIndex: 'projectedPriceBefore', width: 110},
  ];

  return (
    <div className={styles.modal}>
      <h1>{isNewSource ? 'Add source and picks' : 'Bulk upsert picks'}</h1>
      <p className={styles.modalSubtitle}>
        {recommendation?.name} · {recommendation?.exchange?.code}
      </p>

      <Form
        form={form}
        layout="vertical"
        onFinish={submit}
        initialValues={{
          source,
          type: 'BUY',
          language: recommendation?.language || 'EN',
          defaultSource: !registeredSources.length,
          imageUrl: registeredSource?.imageUrl,
        }}>
        <div className={styles.formRow}>
          <Form.Item
            label="Source"
            name="source"
            className={styles.grow}
            rules={[{required: true, message: 'Select a source'}]}>
            <Select
              disabled={!isNewSource}
              options={sourceOptions}
              placeholder="Select a source"
            />
          </Form.Item>

          <Form.Item
            label="Pick type"
            name="type"
            rules={[{required: true, message: 'Select a type'}]}>
            <Radio.Group
              optionType="button"
              options={PICK_TYPES.map(type => ({
                label: typeLabel(type),
                value: type,
              }))}
            />
          </Form.Item>
        </div>

        <div className={styles.formRow}>
          <Form.Item
            label="Analysis language"
            name="language"
            className={styles.grow}
            rules={[{required: true, message: 'Select a language'}]}>
            <Select options={LANGUAGE_OPTIONS} />
          </Form.Item>

          <Form.Item
            label="Make this the default source"
            name="defaultSource"
            valuePropName="checked"
            tooltip={
              registeredSources.length
                ? 'The default source is what users see first on the recommendation.'
                : 'The first source registered always becomes the default.'
            }>
            <Switch disabled={!registeredSources.length} />
          </Form.Item>
        </div>

        <Form.Item label="Source logo" name="imageUrl" className={styles.grow}>
          <SourceLogoField />
        </Form.Item>

        <div className={styles.entryModeRow}>
          <Radio.Group
            optionType="button"
            value={entryMode}
            onChange={event => handleEntryModeChange(event.target.value)}
            options={[
              {label: 'Manual rows', value: 'MANUAL'},
              {label: 'CSV import', value: 'CSV'},
            ]}
          />
          {entryMode === 'CSV' && (
            <Button
              label="Download sample CSV"
              type="secondary"
              onClick={downloadSampleCsv}
            />
          )}
        </div>

        {!isNewSource && (
          <Alert
            className={styles.alert}
            type="info"
            showIcon
            message="Submitted rows are added or updated — instruments left out of this list stay as they are. Remove a pick explicitly to take it off the recommendation."
          />
        )}

        {entryMode === 'MANUAL' ? (
          <Form.List name="picks">
            {(fields, {add, remove}) => (
              <>
                {fields.map(({key, name}) => (
                  <div key={key} className={styles.pickRow}>
                    <div className={styles.pickRowHeader}>
                      <Tag className={styles.softTag}>Pick {name + 1}</Tag>
                      <img
                        src={trashIcon}
                        alt="remove"
                        className={styles.trash}
                        onClick={() => remove(name)}
                      />
                    </div>
                    <PickFields
                      form={form}
                      namePath={[name]}
                      fieldPath={['picks', name]}
                      exchange={recommendation?.exchange}
                    />
                  </div>
                ))}
                <AntdButton onClick={() => add({analysis: ''})}>
                  Add pick row
                </AntdButton>
                {fields.length > MAX_PICKS_PER_REPORT && (
                  <Alert
                    className={styles.alert}
                    type="warning"
                    showIcon
                    message={`Reports read at most ${MAX_PICKS_PER_REPORT} picks per source and type — picks beyond that would be missing from the PDF.`}
                  />
                )}
              </>
            )}
          </Form.List>
        ) : (
          <div className={styles.csvSection}>
            <Upload
              accept=".csv"
              maxCount={1}
              showUploadList={false}
              beforeUpload={() => false}
              onChange={handleCsvSelected}>
              <div className={styles.csvUpload}>
                <img src={cloudIcon} alt="" />
                <span>{csvFile ? csvFile.name : 'Choose a CSV file'}</span>
                <span className={styles.mutedText}>
                  Columns, in order: instrumentId, priceAsOf, priceAsOfDate,
                  projectedPriceBefore, projectedPriceBeforeDate, analysis
                </span>
              </div>
            </Upload>

            {csvResult?.fileErrors?.map(fileError => (
              <Alert
                key={fileError}
                className={styles.alert}
                type="error"
                showIcon
                message={fileError}
              />
            ))}

            {csvResult?.rows?.length > 0 && (
              <>
                {csvRowErrors.length > 0 && (
                  <Alert
                    className={styles.alert}
                    type="error"
                    showIcon
                    message={`${csvRowErrors.length} of ${csvResult.rows.length} rows need fixing before this file can be imported.`}
                  />
                )}
                {csvResult.rows.length > MAX_PICKS_PER_REPORT && (
                  <Alert
                    className={styles.alert}
                    type="warning"
                    showIcon
                    message={`This file has ${csvResult.rows.length} picks. Reports read at most ${MAX_PICKS_PER_REPORT} per source and type.`}
                  />
                )}
                <Table
                  size="small"
                  rowKey="line"
                  className={styles.table}
                  pagination={false}
                  scroll={{y: 260}}
                  columns={csvPreviewColumns}
                  dataSource={csvResult.rows}
                />
              </>
            )}
          </div>
        )}

        <div className={styles.formFooter}>
          <SubmitButton
            smallWidth
            disabled={loading || uploading}
            label={
              uploading
                ? 'Uploading CSV…'
                : loading
                ? 'Submitting…'
                : isNewSource
                ? 'Add source and picks'
                : `Upsert ${sourceLabel(source)} picks`
            }
          />
        </div>
      </Form>
    </div>
  );
};

/**
 * Optional source logo. The stored value is a URL either way, so it can be
 * pasted directly — a source's logo usually already lives somewhere — or
 * uploaded, which just fills the same field with the bucket URL.
 */
const SourceLogoField = ({value, onChange}) => {
  const [uploading, setUploading] = useState(false);

  const handleUpload = async info => {
    const file = info.file?.originFileObj || info.file;
    if (!file) return;
    try {
      setUploading(true);
      const url = await UploadDocumentToBucket(
        file,
        () => {},
        BUCKET,
        true,
        'recommendations',
      );
      onChange?.(url);
    } catch (error) {
      toast.error('Could not upload that image');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className={styles.logoField}>
      <Upload
        accept=".png,.jpg,.jpeg"
        maxCount={1}
        showUploadList={false}
        beforeUpload={() => false}
        onChange={handleUpload}>
        <div className={styles.logoUpload}>
          {value ? (
            <img src={value} alt="source logo" className={styles.logoPreview} />
          ) : (
            <img src={cloudIcon} alt="" />
          )}
          <span>
            {uploading
              ? 'Uploading…'
              : value
              ? 'Replace logo'
              : 'Upload a logo (optional)'}
          </span>
        </div>
      </Upload>
      {value && !uploading && (
        <AntdButton
          type="text"
          className={styles.logoRemove}
          onClick={() => onChange?.(undefined)}>
          <img src={trashIcon} alt="Remove logo" />
        </AntdButton>
      )}
    </div>
  );
};

export default SourcePicksModal;
